/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Observable, forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { UserStateService } from 'src/app/shared/services/user-state.service';
import { BreakpointService } from 'src/app/shared/services/breakpoints.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import { ScoreUtilsService } from 'src/app/shared/utils/score-utils.service';
import {
  getScoresInfo,
  resolveScoreTypeKey,
} from 'src/app/shared/utils/score-type-details';
import { NewsScoreStateService } from '../../news-score-state.service';
import { ScoreFocusedEventsService } from '../score-focused-events.service';
import { ChartmapTopEventsService } from './chartmap-top-events.service';
import {
  SORT_EVENTS_BY_TOGGLE,
  TemporalityWidths,
  computeTemporalityWidths,
  hasTemporalityAnalytics,
  scrollIntoViewIfNotVisible,
} from './chartmap-mosaic-shared';

/**
 * "Score Events" mosaic extracted from chartmap-company. Renders the top
 * events for the score on screen — the user's default, or a URL-focused score
 * (alert link / `?score=` selection) via `displayScore`.
 * Owns the scorelab top-events fetches (incl. ESG fan-out and credit-risk
 * sign handling), migrated off `yukkApi.scoresTimeSeries` onto
 * `ChartmapTopEventsService` / `NewsScoreStateService`.
 */
@Component({
  selector: 'app-score-events-mosaic',
  templateUrl: './score-events-mosaic.component.html',
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScoreEventsMosaicComponent implements OnChanges, AfterViewChecked {
  @ViewChild('expansionContainer') expansionContainer: ElementRef;
  contentHeight: number;

  /** Live URL params (display bindings). */
  @Input() qparam: any;
  /** Gated refresh trigger: set by the parent only when a refetch is due. */
  @Input() fetchParams: any;
  @Input() scoreVisible = false;
  /** True when score-events is the only open mosaic — drives the tiny graphs. */
  @Input() soleMosaic = false;
  /** Mosaic state maps shared with the parent (parent initializes per refresh). */
  @Input() nodata: any = {};
  @Input() loading: any = {};
  @Input() iready: any = {};
  @Input() eventsSortedBy = 'importance';
  @Output() eventsSortedByChange = new EventEmitter<string>();

  scoreEvents: any;
  scoreEventsESG: any;
  scoreEventsE: any;
  scoreEventsS: any;
  scoreEventsG: any;
  scoresESG = {
    scoreESG: null,
    scoreE: null,
    scoreS: null,
    scoreG: null,
    scoreLowest: '',
  };

  customScore: string;
  sortEventsByToogle = SORT_EVENTS_BY_TOGGLE;
  eventSeriesMap: Map<string, [number, number][]> = new Map();
  focusedTimeframe = '';
  screenSize$ = this.breakpointService.screenSize$;

  /** Tiny-graph window = the global timeframe (no focus, no override). */
  get microTimeframe(): string {
    return this.qparam?.time
      ? String(DateUtils.fromTo(this.qparam.time).period)
      : '7';
  }

  constructor(
    private router: Router,
    public routing: RoutingService,
    public featureFlagsService: FeatureFlagsService,
    public userState: UserStateService,
    private breakpointService: BreakpointService,
    private topEvents: ChartmapTopEventsService,
    private newsScoreState: NewsScoreStateService,
    private scoreFocusedEventsService: ScoreFocusedEventsService,
    private scoreUtils: ScoreUtilsService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;
    this.scoreFocusedEventsService.eventSeriesMap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((seriesMap) => {
        this.eventSeriesMap = seriesMap;
        this.cdr.markForCheck();
      });
    this.scoreFocusedEventsService.timeframe$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tf) => {
        this.focusedTimeframe = tf ?? '';
        this.cdr.markForCheck();
      });
    this.destroyRef.onDestroy(() => this.cancel$.complete());
  }

  private lastFetchedParams: any;
  // Cancels the in-flight fetch on a new refresh, a ranking toggle, or hide.
  private cancel$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    const anyVisible = this.scoreVisible;
    const visibleChanged = !!changes['scoreVisible'];

    // Hidden: kill any in-flight fetch and clear state so a re-show can't flash
    // stale data, and we don't fetch for a mosaic that isn't displayed.
    if (visibleChanged && !anyVisible) {
      this.onHidden();
      return;
    }

    // Lazy: fetch on a new fetchParams cycle or when it first becomes visible
    // with params set — but never refetch the same params.
    const paramsChanged = !!(
      (changes['fetchParams'] || visibleChanged) &&
      anyVisible &&
      this.fetchParams &&
      this.fetchParams !== this.lastFetchedParams
    );
    // Only the active ranking is fetched, so switching Count↔Importance must
    // re-fetch (the other ranking wasn't pre-loaded). Don't touch
    // lastFetchedParams here — the params themselves didn't change.
    const rankingChanged = !!(
      changes['eventsSortedBy'] &&
      !changes['eventsSortedBy'].firstChange &&
      this.fetchParams &&
      anyVisible
    );
    if (paramsChanged) {
      this.lastFetchedParams = this.fetchParams;
    }
    if (paramsChanged || rankingChanged) {
      this.refresh();
    }
  }

  private onHidden(): void {
    this.cancel$.next();
    ['Score Events'].forEach((key) => {
      this.loading[key] = false;
      this.iready[key] = false;
      this.nodata[key] = false;
    });
    this.lastFetchedParams = undefined;
    this.cdr.markForCheck();
  }

  /** Active top-events ranking, driven by the current sort selection. */
  private get activeRanking(): 'absolute_count' | 'relative_importance' {
    return this.eventsSortedBy === 'count'
      ? 'absolute_count'
      : 'relative_importance';
  }

  /**
   * Wraps the parity engine, injecting the active ranking into every fetch.
   * Defaults the score to the one on screen (URL focus or user default) unless
   * the caller already pinned a specific score (`requestScore`) or sub-score
   * (`scoreType`, e.g. the E/S/G breakdown) — so a focused/alert score drives
   * the mosaic instead of always falling back to the default.
   */
  private fetchTopEventsRanked(
    qparam: any,
    opts: Record<string, any> = {},
  ): Observable<any> {
    const scoped =
      opts.requestScore || opts.scoreType
        ? opts
        : { ...opts, requestScore: this.displayScore };
    return this.topEvents.fetchTopEvents(qparam, {
      ...scoped,
      ranking: this.activeRanking,
    });
  }

  ngAfterViewChecked() {
    if (this.expansionContainer) {
      this.contentHeight = Math.max(
        this.expansionContainer.nativeElement.offsetHeight / 7 - 1,
        40,
      );
      this.cdr.detectChanges();
    }
  }

  private refresh(): void {
    // Cancel any in-flight fetch from the previous refresh / ranking toggle.
    this.cancel$.next();
    const qparam = this.fetchParams;
    this.customScore = this.featureFlagsService.scorelabSettings.defaultScore;

    const entityish =
      this.routing.isFolio() ||
      (qparam.type &&
        (qparam.type === 'company' ||
          qparam.type === 'pne' ||
          ['industry', 'supersector', 'sector', 'subsector'].includes(
            qparam.type,
          ))) ||
      qparam.isin;

    // SCORELAB EVENTS ---------------------------------------

    if (!(this.displayScore === 'sentiment') && entityish) {
      // Show the loading state for the duration of the (re-)fetch. The parent
      // resets this on qparam change, but a Count↔Importance toggle re-fetches
      // without a parent reset — without this, the stale ranking's data renders
      // in the new sort layout until the request lands (visible glitch).
      this.iready['Score Events'] = false;
      this.loading['Score Events'] = true;
      this.nodata['Score Events'] = false;
      if (this.isEsgSetup()) {
        this.fetchEsgScoreEvents();
      } else if (this.displayScore === 'credit_risk') {
        this.fetchCreditRiskScoreEvents();
      } else {
        this.fetchDefaultScoreEvents();
      }
    }
  }

  // -----------------------------------------------------------------------
  // Score events pipelines
  // -----------------------------------------------------------------------

  private fetchEsgScoreEvents(): void {
    forkJoin({
      esgEvents: this.fetchTopEventsRanked(this.qparam, {
        withAllEvents: true,
      }),
      esgScores: this.fetchScoreV2(this.qparam),
      eEvents: this.fetchTopEventsRanked(this.qparam, {
        scoreType: 'e_standard',
      }),
      eScores: this.fetchScoreV2(this.qparam, 'e_standard'),
      sEvents: this.fetchTopEventsRanked(this.qparam, {
        scoreType: 's_standard',
      }),
      sScores: this.fetchScoreV2(this.qparam, 's_standard'),
      gEvents: this.fetchTopEventsRanked(this.qparam, {
        scoreType: 'g_standard',
      }),
      gScores: this.fetchScoreV2(this.qparam, 'g_standard'),
    })
      .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          const resCopy2 = JSON.parse(JSON.stringify(result));
          this.scoreEventsESG = resCopy2.esgEvents.top_events
            ? resCopy2.esgEvents.top_events
            : [];
          this.scoresESG.scoreESG = resCopy2.esgScores.score_ts_last_score.score
            ? resCopy2.esgScores.score_ts_last_score.score.toFixed(1)
            : null;
          this.scoreEventsE = resCopy2.eEvents.top_events
            ? resCopy2.eEvents.top_events
            : [];
          this.scoresESG.scoreE = resCopy2.eScores.score_ts_last_score.score
            ? resCopy2.eScores.score_ts_last_score.score.toFixed(1)
            : null;
          this.scoreEventsS = resCopy2.sEvents.top_events
            ? resCopy2.sEvents.top_events
            : [];
          this.scoresESG.scoreS = resCopy2.sScores.score_ts_last_score.score
            ? resCopy2.sScores.score_ts_last_score.score.toFixed(1)
            : null;
          this.scoreEventsG = resCopy2.gEvents.top_events
            ? resCopy2.gEvents.top_events
            : [];
          this.scoresESG.scoreG = resCopy2.gScores.score_ts_last_score.score
            ? resCopy2.gScores.score_ts_last_score.score.toFixed(1)
            : null;
          this.scoresESG.scoreLowest = [
            { type: 'e', score: this.scoresESG.scoreE },
            { type: 's', score: this.scoresESG.scoreS },
            { type: 'g', score: this.scoresESG.scoreG },
          ].reduce((min, x) => (min.score < x.score ? min : x)).type;
          let nonEmpty = false;
          this.scoreEventsESG.forEach((item) => {
            if (item.contrib_amount > 0) {
              nonEmpty = true;
            }
          });
          this.scoreEventsE.forEach((item) => {
            if (item.contrib_amount > 0) {
              nonEmpty = true;
            }
          });
          this.scoreEventsS.forEach((item) => {
            if (item.contrib_amount > 0) {
              nonEmpty = true;
            }
          });
          this.scoreEventsG.forEach((item) => {
            if (item.contrib_amount > 0) {
              nonEmpty = true;
            }
          });
          this.nodata['Score Events'] = false;

          // showing chosen event for event based alert at the top of events mosaic
          if (
            this.qparam?.score &&
            this.qparam?.scoreevent &&
            this.qparam?.eventid
          ) {
            const isESG = this.qparam.score === 'esg' ? 'esg' : '';
            const targetId = this.qparam.eventid + '-' + isESG;
            setTimeout(() => {
              const targetElement = document.getElementById(targetId);
              scrollIntoViewIfNotVisible(targetElement);
            }, 500);
          }

          if (nonEmpty) {
            this.iready['Score Events'] = true;
          } else {
            this.iready['Score Events'] = false;
            this.loading['Score Events'] = false;
          }
          this.cdr.markForCheck();

          const timeframe = this.qparam.time ? this.qparam.time : 7;
          const timeObj = DateUtils.fromTo(timeframe);
          const timeParam =
            moment(timeObj.dateFrom)
              .subtract(timeObj.period, 'days')
              .format('YYYY-MM-DD') +
            '--' +
            timeObj.dateFrom;

          forkJoin({
            esgEvents: this.fetchTopEventsRanked(this.qparam, {
              withAllEvents: true,
              time: timeParam,
            }),
            eEvents: this.fetchTopEventsRanked(this.qparam, {
              time: timeParam,
              scoreType: 'e_standard',
            }),
            sEvents: this.fetchTopEventsRanked(this.qparam, {
              time: timeParam,
              scoreType: 's_standard',
            }),
            gEvents: this.fetchTopEventsRanked(this.qparam, {
              time: timeParam,
              scoreType: 'g_standard',
            }),
          })
            .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
            .subscribe((result2: any) => {
              this.applyPrevContrib(this.scoreEventsESG, result2['esgEvents']);
              this.applyPrevContrib(this.scoreEventsE, result2['eEvents']);
              this.applyPrevContrib(this.scoreEventsS, result2['sEvents']);
              this.applyPrevContrib(this.scoreEventsG, result2['gEvents']);
              this.cdr.markForCheck();
            });
        },
        error: () => {
          this.nodata['Score Events'] = true;
          this.loading['Score Events'] = false;
          this.cdr.markForCheck();
        },
      });
  }

  private fetchCreditRiskScoreEvents(): void {
    this.fetchScoreV2(this.qparam)
      .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const crScore =
            result.score_ts_last_score?.score !== null
              ? result.score_ts_last_score?.score
              : null;

          this.fetchTopEventsRanked(this.qparam, {
            withAllEvents: true,
            topEventsSign: crScore && crScore > 0 ? 'neg' : null,
          })
            .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (result2: any) => {
                this.scoreEvents = result2.top_events
                  ? JSON.parse(JSON.stringify(result2.top_events))
                  : [];
                let nonEmpty = false;
                this.scoreEvents.forEach((item) => {
                  if (item.contrib_amount > 0) {
                    nonEmpty = true;
                  }
                });
                this.nodata['Score Events'] = false;
                if (nonEmpty) {
                  this.iready['Score Events'] = true;
                } else {
                  this.iready['Score Events'] = false;
                  this.loading['Score Events'] = false;
                }
                this.cdr.markForCheck();

                const timeframe = this.qparam.time ? this.qparam.time : 7;
                const timeObj = DateUtils.fromTo(timeframe);
                const timeParam =
                  moment(timeObj.dateFrom)
                    .subtract(timeObj.period, 'days')
                    .format('YYYY-MM-DD') +
                  '--' +
                  timeObj.dateFrom;

                this.fetchTopEventsRanked(this.qparam, {
                  withAllEvents: true,
                  time: timeParam,
                  topEventsSign: crScore && crScore > 0 ? 'neg' : null,
                })
                  .pipe(
                    takeUntil(this.cancel$),
                    takeUntilDestroyed(this.destroyRef),
                  )
                  .subscribe((result3: any) => {
                    this.applyPrevContrib(this.scoreEvents, result3);
                    this.cdr.markForCheck();
                  });
              },
              error: () => {
                this.nodata['Score Events'] = true;
                this.loading['Score Events'] = false;
                this.cdr.markForCheck();
              },
            });
        },
        error: () => {
          this.nodata['Score Events'] = true;
          this.loading['Score Events'] = false;
          this.cdr.markForCheck();
        },
      });
  }

  private fetchDefaultScoreEvents(): void {
    this.fetchTopEventsRanked(this.qparam, { withAllEvents: true })
      .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          this.scoreEvents = result.top_events
            ? JSON.parse(JSON.stringify(result.top_events))
            : [];
          let nonEmpty = false;
          this.scoreEvents.forEach((item) => {
            if (item.contrib_amount > 0) {
              nonEmpty = true;
            }
          });
          this.nodata['Score Events'] = false;
          if (nonEmpty) {
            this.iready['Score Events'] = true;
          } else {
            this.iready['Score Events'] = false;
            this.loading['Score Events'] = false;
          }
          this.cdr.markForCheck();

          const timeframe = this.qparam.time ? this.qparam.time : 7;
          const timeObj = DateUtils.fromTo(timeframe);
          const timeParam =
            moment(timeObj.dateFrom)
              .subtract(timeObj.period, 'days')
              .format('YYYY-MM-DD') +
            '--' +
            timeObj.dateFrom;

          this.fetchTopEventsRanked(this.qparam, {
            withAllEvents: true,
            time: timeParam,
          })
            .pipe(takeUntil(this.cancel$), takeUntilDestroyed(this.destroyRef))
            .subscribe((result2: any) => {
              this.applyPrevContrib(this.scoreEvents, result2);
              this.cdr.markForCheck();
            });
        },
        error: () => {
          this.nodata['Score Events'] = true;
          this.loading['Score Events'] = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Merge previous-period contributions into the current events (legacy delta pattern). */
  private applyPrevContrib(events: any[], prevResult: any): void {
    events.forEach((event, index) => {
      if (
        prevResult['top_events'].filter((top) => top.id === event.id).length > 0
      ) {
        events[index].contrib_amount_prev = prevResult['top_events'].filter(
          (top) => top.id === event.id,
        )[0].contrib_amount;
        events[index].contrib_amount_importance_prev = prevResult[
          'top_events'
        ].filter((top) => top.id === event.id)[0].contrib_amount_importance;
      }
    });
  }

  /**
   * Legacy-compatible score fetch via the v2 score_ts state service.
   */
  private fetchScoreV2(
    params: any,
    scoreTypeOverride?: string,
  ): Observable<any> {
    const scoreTypeParam = scoreTypeOverride || params.requestScore || 'esg';
    const resolvedScoreType =
      this.newsScoreState.resolveScoreType(scoreTypeParam);
    const timeParam = params.time;

    return this.newsScoreState
      .fetchScoreDataWithTime(resolvedScoreType, timeParam)
      .pipe(
        map((state) => ({
          score_ts_last_score: {
            score: state.lastScore,
          },
          score_ts: state.scoreData.map((item) => ({ score: item.score })),
        })),
        catchError((error) => {
          console.error('Error fetching v2 score:', error);
          return of({
            score_ts_last_score: { score: null },
            score_ts: [],
          });
        }),
      );
  }

  // -----------------------------------------------------------------------
  // Template helpers
  // -----------------------------------------------------------------------

  sortEventsBy(option) {
    this.eventsSortedByChange.emit(option);
  }

  /** get events mosaic title for current default score */
  scoreEventsTitle() {
    const info = getScoresInfo(
      this.featureFlagsService.scorelabSettings,
      this.displayScore,
    ) as { name: string };
    return (info?.name ?? '') + ' Events';
  }

  /**
   * The score the mosaic should display: a URL-focused score (alert link or
   * in-app `?score=` selection) takes precedence over the user's default,
   * normalised to the canonical cockpit id (e.g. `bull_bear` → `bb`).
   */
  get displayScore(): string {
    return this.qparam?.score
      ? resolveScoreTypeKey(this.qparam.score)
      : this.customScore;
  }

  isEsgSetup() {
    return this.displayScore === 'esg';
  }

  // Only predefined scores have an `o_<id>.svg` icon; custom score uids 404.
  isPredefinedScore(id: string): boolean {
    return !!id && this.scoreUtils.isPredefinedScore(id);
  }

  /** click specific scorelab event */
  iScoreEvent(item) {
    let eventid = null;
    let eventype = null;
    let scoreevent = null;
    let newstype = ['industry', 'sector', 'supersector', 'subsector'].includes(
      this.qparam.type,
    )
      ? null
      : this.qparam.newstype;

    if (this.qparam.eventid !== item.id) {
      eventid = item.id;
      eventype = item.type;
      scoreevent = true;
      if (!this.routing.isFolio() || this.qparam?.portfolioId) {
        newstype = 'Events';
      }
    }

    this.router.navigate([], {
      queryParams: {
        eventid: eventid,
        eventype: eventype,
        scoreevent: scoreevent,
        newstype: newstype,
        newsday: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  panelMax(list: any[]): number {
    if (!list?.length) return 0;
    const key =
      this.eventsSortedBy === 'count'
        ? 'contrib_amount'
        : 'contrib_amount_importance';
    return Math.max(0, ...list.map((event) => event[key] ?? 0));
  }

  // Memoized per analytics object (the chart replaces it wholesale) so rows get
  // stable references under OnPush and the math runs once per event, not per CD.
  private temporalityCache = new WeakMap<
    object,
    Map<string, TemporalityWidths>
  >();

  temporalityWidths(itemId: string): TemporalityWidths | null {
    const analytics = this.userState.scoreFocusedEventsAnalytics;
    if (!analytics) return null;
    let byId = this.temporalityCache.get(analytics);
    if (!byId) {
      byId = new Map();
      this.temporalityCache.set(analytics, byId);
    }
    let widths = byId.get(itemId);
    if (!widths) {
      widths = computeTemporalityWidths(analytics, itemId);
      byId.set(itemId, widths);
    }
    return widths;
  }

  get hasTemporalityAnalytics(): boolean {
    return hasTemporalityAnalytics(this.userState.scoreFocusedEventsAnalytics);
  }
}
